import { useMemo } from "react"
import { formatDateString } from "@/v2-common/utils/utils.date"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import Table, {
  createColumns,
  summaryFraction,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import Badge from "@/v2-backoffice-client/components/badge"
import LicenseSeatBadge from "@/v2-backoffice-client/license/LicenseSeatBadge"
import LicenseStatusBadge from "@/v2-backoffice-client/license/LicenseStatusBadge"
import { LICENSE_STATUS_COLUMN_SIZE } from "@/v2-backoffice-client/license/license.constants"
import { useLicenseStatusesQuery } from "@/v2-backoffice-client/license/license.statuses.query"
import { useLicensePlanCustomersQuery } from "@/v2-backoffice-client/license/plan/view/license.plan.customers.query"
import CustomerValueBadge from "@/v2-backoffice-client/customer/billing/CustomerValueBadge"
import CustomerPaymentBadge from "@/v2-backoffice-client/customer/billing/CustomerPaymentBadge"
import { formatInvoiceAmount } from "@/v2-backoffice-client/customer/billing/customerBilling.utils"
import type { LicensePlanCustomersQuery } from "@/v2-backoffice-client/license/plan/view/__types__/license.plan.customers.query"

export type LicensePlanCustomerRow =
  LicensePlanCustomersQuery["getLicensePlanCustomers"][number]

function createLicensePlanCustomerColumns(input: {
  statusNamesByCode: Map<number, string>
}) {
  const { statusNamesByCode } = input

  function statusName(customer: LicensePlanCustomerRow) {
    return (
      statusNamesByCode.get(customer.LicenseStatusCode) ??
      String(customer.LicenseStatusCode)
    )
  }

  return createColumns<LicensePlanCustomerRow>({
    enableSorting: true,
    enableHiding: true,
    columns: {
      CustomerName: {
        id: "CustomerName",
        label: "Customer",
        defaultSize: 200,
        // Push onto the sheet stack — a CSM walks license → customer and back.
        // App-created customers without a HubSpot id can't deep-link (the
        // customer panel is keyed by it).
        to: ({ row }) =>
          row.original.ExternalCustomerId
            ? `>>?v=customer:${row.original.ExternalCustomerId}`
            : undefined,
      },
      Mrr: {
        id: "Mrr",
        label: "MRR",
        description:
          "What this customer pays for THIS license per month (live Fenerum subscriptions on the license's terms) — not their whole book.",
        defaultSize: 130,
        renderCell({ row }) {
          const { Mrr, MrrCurrency, IsPrimaryPlan, LicensePlanName } =
            row.original
          if (Mrr == null) return null
          return (
            <span className="flex items-center gap-1.5">
              <span className="tabular-nums">
                {formatInvoiceAmount(Mrr, MrrCurrency)}
              </span>
              {!IsPrimaryPlan && (
                <Badge
                  size="sm"
                  variant="neutral"
                  tooltip={
                    LicensePlanName
                      ? `Billed through a companion subscription on the customer's ${LicensePlanName} license.`
                      : "Billed through a companion subscription on another license."
                  }
                >
                  add-on
                </Badge>
              )}
            </span>
          )
        },
        exportValue({ row }) {
          return row.original.Mrr ?? ""
        },
      },
      // Unit prices, derived from columns already on the row: what this
      // customer pays per licensed seat and per actual human — the numbers
      // that make same-license customers comparable at a glance (two customers
      // on identical MRR read very differently at 5 vs 50 users).
      MrrPerSeat: {
        id: "MrrPerSeat",
        label: "Per Seat",
        description:
          "This license's MRR divided by the licensed seat cap — the effective seat price this customer negotiated. Empty = no MRR or uncapped.",
        defaultSize: 100,
        getValue: (row) =>
          row.Mrr != null && row.Seats ? row.Mrr / row.Seats : null,
        renderCell({ row }) {
          const { Mrr, Seats, MrrCurrency } = row.original
          if (Mrr == null || !Seats) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(Math.round(Mrr / Seats), MrrCurrency)}
            </span>
          )
        },
        exportValue({ row }) {
          const { Mrr, Seats } = row.original
          return Mrr != null && Seats ? Math.round(Mrr / Seats) : ""
        },
      },
      MrrPerUser: {
        id: "MrrPerUser",
        label: "Per User",
        description:
          "This license's MRR divided by the customer's registered users — what each actual human costs them. High = room to grow usage; low vs license peers = an underpriced account.",
        defaultSize: 100,
        getValue: (row) =>
          row.Mrr != null && row.UserCount > 0 ? row.Mrr / row.UserCount : null,
        renderCell({ row }) {
          const { Mrr, UserCount, MrrCurrency } = row.original
          if (Mrr == null || UserCount <= 0) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(Math.round(Mrr / UserCount), MrrCurrency)}
            </span>
          )
        },
        exportValue({ row }) {
          const { Mrr, UserCount } = row.original
          return Mrr != null && UserCount > 0 ? Math.round(Mrr / UserCount) : ""
        },
      },
      LicenseStatusCode: {
        id: "LicenseStatusCode",
        label: "Status",
        defaultSize: LICENSE_STATUS_COLUMN_SIZE,
        renderCell({ row }) {
          return (
            <LicenseStatusBadge
              license={row.original}
              statusName={statusNamesByCode.get(row.original.LicenseStatusCode)}
            />
          )
        },
        exportValue({ row }) {
          return statusName(row.original)
        },
        summaryValue({ row }) {
          return statusName(row.original)
        },
      },
      Seats: {
        id: "Seats",
        label: "Seats",
        description:
          "Occupied seats (active users + open invitations) against the licensed cap. No cap = uncapped.",
        defaultSize: 80,
        // Composes the ONE seat badge (LicenseSeatBadge) so seat pressure
        // wears the same colors here as on the customers board.
        renderCell({ row }) {
          const customer = row.original
          const occupied = customer.UserCount + customer.PendingInvitationCount
          return <LicenseSeatBadge occupied={occupied} cap={customer.Seats} />
        },
        exportValue({ row }) {
          const customer = row.original
          const occupied = customer.UserCount + customer.PendingInvitationCount
          return `${occupied} / ${customer.Seats ?? "—"}`
        },
        summaryValue({ row }) {
          const customer = row.original
          const occupied = customer.UserCount + customer.PendingInvitationCount
          return summaryFraction(occupied, customer.Seats)
        },
      },
      ValueTier: {
        id: "ValueTier",
        label: "Value",
        description:
          "Where this customer's 12-month billed revenue (whole book, all licenses) ranks among all actively billed customers.",
        defaultSize: 90,
        renderCell({ row }) {
          return <CustomerValueBadge customer={row.original} />
        },
      },
      PaymentHealth: {
        id: "PaymentHealth",
        label: "Payment",
        defaultSize: 90,
        renderCell({ row }) {
          return <CustomerPaymentBadge customer={row.original} />
        },
      },
      RenewsAt: {
        id: "RenewsAt",
        label: "Renews",
        defaultSize: 90,
        renderCell({ row }) {
          const value = row.original.RenewsAt
          return value ? formatDateString(value) : null
        },
      },
      StartedAt: {
        id: "StartedAt",
        label: "Started",
        defaultSize: 90,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.StartedAt
          return value ? formatDateString(value) : null
        },
      },
      TrialEndsAt: {
        id: "TrialEndsAt",
        label: "Trial Ends",
        defaultSize: 90,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.TrialEndsAt
          return value ? formatDateString(value) : null
        },
      },
      LicensePlanName: {
        id: "LicensePlanName",
        label: "Via License",
        description:
          "The license the money hangs on — differs from this one for companion (per-seat) subscriptions. Links to that license's panel.",
        defaultSize: 140,
        defaultHidden: true,
        // Only companion rows link — a primary row's license IS the open panel.
        to: ({ row }) =>
          !row.original.IsPrimaryPlan && row.original.LicensePlanId
            ? `?v=plan:${row.original.LicensePlanId}`
            : undefined,
      },
      Billed12M: {
        id: "Billed12M",
        label: "Billed 12M (all licenses)",
        defaultSize: 120,
        defaultHidden: true,
        renderCell({ row }) {
          const value = row.original.Billed12M
          if (value == null) return null
          return (
            <span className="tabular-nums">
              {formatInvoiceAmount(value, row.original.PrimaryCurrency)}
            </span>
          )
        },
      },
      CustomerId: {
        id: "CustomerId",
        label: "Customer Id",
        defaultSize: 70,
        defaultHidden: true,
      },
    },
  })
}

export type LicensePlanCustomerTableProps = Omit<
  TableProps<LicensePlanCustomerRow>,
  "data" | "columns"
> & {
  planId: number
  exportFileName?: string
  /**
   * Called alongside the table's own refetch on toolbar refresh — the sheet
   * hooks in the license-row refetch so the badge counts and MRR stat cards
   * refresh with the list they summarize.
   */
  onRefresh?(): void
}

// The license panel's Customers tab: who is on the license and what each of them
// pays for it, most valuable first (proc order). Value/Payment reuse the
// customer boards' badges — same derivation, same presentation. Owns its
// list query (plus the status-name lookup) — the data loads when the tab is
// first opened, not when the panel is.
function LicensePlanCustomerTable(props: LicensePlanCustomerTableProps) {
  const { className, planId, exportFileName, onRefresh, ...tableProps } = props

  const { data, loading, error, refetch } = useLicensePlanCustomersQuery({
    variables: { planId },
  })
  const customers = data?.getLicensePlanCustomers ?? []

  const { data: statusesData } = useLicenseStatusesQuery()
  const statusNamesByCode = useMemo(
    () =>
      new Map(
        (statusesData?.getLicenseStatuses ?? []).map((status) => [
          status.Code,
          status.Name,
        ]),
      ),
    [statusesData?.getLicenseStatuses],
  )

  const columns = useMemo(
    () => createLicensePlanCustomerColumns({ statusNamesByCode }),
    [statusNamesByCode],
  )

  return (
    <Table
      className={cn("license-plan-customer-table", className)}
      tableId="license-plan-customer-table"
      data={customers}
      columns={columns}
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.Search
          column="CustomerName"
          placeholder="Search by customer name..."
        />

        <Table.ToolbarFilters />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu
          onRefresh={() => {
            void refetch()
            onRefresh?.()
          }}
          exportFileName={exportFileName ?? "license-customers"}
        />
      </Table.Toolbar>

      <Table.FilterList />

      <Table.Content isLoading={loading} error={error} />
    </Table>
  )
}

export default LicensePlanCustomerTable
